/**
 * VehiclesAdmin.tsx — ADMIN VEHICLES screen (v2.3.4).
 *
 * Admin-gated exactly like Billing Rules: the SERVER returns 403 to a tech or a
 * dispatcher on every write here (and on GET /vehicles/admin/all), so hiding the
 * UI is a convenience, not the control.
 *
 * What an admin can do here:
 *   - see EVERY vehicle, including every tech-entered one, with the owning tech
 *   - create COMPANY vehicles (nickname + license plate are BOTH required)
 *   - assign / unassign a vehicle to one OR MANY personnel
 *   - edit rental end dates
 *   - retire (deactivate) / reactivate a vehicle
 *   - see the FUEL LOGS per vehicle / per tech
 *
 * v2.3.5 — FUEL REPORTING + GRADES:
 *   - every fuel log now carries AMOUNT PAID and FUEL GRADE
 *   - derived: COST PER GALLON, MPG (consecutive odometer readings per vehicle),
 *     SPEND PER VEHICLE and SPEND PER TECH  (GET /api/fuel-logs/report)
 *   - the GRADE LIST is an admin setting (PUT /api/fuel-logs/settings) — Lyndon
 *     can add/remove pump grades with no app rebuild
 *
 * BILLING: fuel is a COST record, never a customer charge. Per-TICKET MILEAGE
 * *is* billed — on the ERPNext MILEAGE line — and its rate lives in
 * Admin -> Billing rules.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, Checkbox, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, FormControl, FormControlLabel, Grid, InputLabel,
  ListItemText, MenuItem, OutlinedInput, Select, Snackbar, Switch, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import api from '../services/api';

interface Assignment { id: number; user_id: number; user_name: string; is_current: boolean; }
interface Vehicle {
  id: number; type: 'personal' | 'rental' | 'company';
  nickname: string | null; license_plate: string | null;
  rental_start_date: string | null; rental_end_date: string | null;
  owner_user_id: number | null; owner_name: string | null;
  created_by_role: 'tech' | 'admin'; active: number | boolean;
  assignments: Assignment[]; created_at?: string;
}
interface FuelLog {
  id: number; user_id: number; vehicle_id: number | null; tech_name: string | null;
  vehicle_nickname: string | null; vehicle_plate: string | null;
  gallons: number | null; liters: number | null;
  odometer_miles: number | null; odometer_km: number | null;
  filled_at: string; latitude: number | null; longitude: number | null;
  queued_offline: number;
  // v2.3.5
  amount_paid: number | null; fuel_grade: string | null;
  cost_per_gallon: number | null; mpg: number | null; miles_since_last_fill: number | null;
}
interface VehicleFuelRollup {
  vehicle_id: number; vehicle_label: string; vehicle_plate: string | null; vehicle_type: string;
  fills: number; gallons: number; spend: number; miles: number;
  avg_cost_per_gallon: number | null; mpg: number | null; cost_per_mile: number | null;
  grades: [string, number][]; last_odometer_miles: number | null; last_filled_at: string | null;
}
interface TechFuelRollup {
  user_id: number; tech_name: string; fills: number; gallons: number;
  spend: number; avg_cost_per_gallon: number | null;
}

const money = (v: number | null | undefined) =>
  v === null || v === undefined ? '—' : `$${Number(v).toFixed(2)}`;
const money3 = (v: number | null | undefined) =>
  v === null || v === undefined ? '—' : `$${Number(v).toFixed(3)}`;
const num = (v: number | null | undefined, d = 1) =>
  v === null || v === undefined ? '—' : Number(v).toFixed(d);
interface Employee { id: number; first_name?: string; last_name?: string; email?: string; }

const TYPE_COLOR: Record<string, any> = { company: 'primary', rental: 'warning', personal: 'default' };

export default function VehiclesAdmin() {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [fuel, setFuel] = useState<FuelLog[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState<any>({ type: 'company', nickname: '', license_plate: '', rental_start_date: '', rental_end_date: '', assign_user_ids: [] as number[] });
  const [assignOpen, setAssignOpen] = useState<Vehicle | null>(null);
  const [assignIds, setAssignIds] = useState<number[]>([]);
  const [editOpen, setEditOpen] = useState<Vehicle | null>(null);
  const [editEnd, setEditEnd] = useState('');
  const [fuelFor, setFuelFor] = useState<Vehicle | null>(null);
  // v2.3.5 — reporting + grade settings
  const [perVehicle, setPerVehicle] = useState<VehicleFuelRollup[]>([]);
  const [perTech, setPerTech] = useState<TechFuelRollup[]>([]);
  const [totals, setTotals] = useState<any>(null);
  const [grades, setGrades] = useState<string[]>([]);
  const [gradesOpen, setGradesOpen] = useState(false);
  const [gradesDraft, setGradesDraft] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [v, f, e, g] = await Promise.all([
        api.get('/vehicles/admin/all'),
        api.get('/fuel-logs/report'),
        api.get('/devices/employees').catch(() => ({ data: { employees: [] } })),
        api.get('/fuel-logs/settings').catch(() => ({ data: { fuel_grade_options: [] } })),
      ]);
      setVehicles(v.data.vehicles || []);
      setFuel(f.data.fuel_logs || []);
      setPerVehicle(f.data.per_vehicle || []);
      setPerTech(f.data.per_tech || []);
      setTotals(f.data.totals || null);
      setGrades((g.data as any).fuel_grade_options || []);
      const emp = (e.data as any).employees || (e.data as any) || [];
      setEmployees(Array.isArray(emp) ? emp : []);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load vehicles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const act = async (fn: () => Promise<any>, msg: string) => {
    setBusy(true);
    try {
      await fn();
      setToast(msg);
      await load();
    } catch (err: any) {
      setToast(err?.response?.data?.detail || err?.message || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const empName = (e: Employee) =>
    `${e.first_name || ''} ${e.last_name || ''}`.trim() || e.email || `User ${e.id}`;

  const fuelByVehicle = useMemo(() => {
    const m: Record<number, FuelLog[]> = {};
    fuel.forEach((f) => {
      if (f.vehicle_id == null) return;
      (m[f.vehicle_id] = m[f.vehicle_id] || []).push(f);
    });
    return m;
  }, [fuel]);

  const createValid = () => {
    if (form.type === 'company') return !!form.nickname.trim() && !!form.license_plate.trim();
    if (form.type === 'rental') return !!form.rental_start_date;
    return true;
  };

  const submitCreate = () => {
    const body: any = { type: form.type };
    if (form.nickname.trim()) body.nickname = form.nickname.trim();
    if (form.license_plate.trim()) body.license_plate = form.license_plate.trim();
    if (form.rental_start_date) body.rental_start_date = form.rental_start_date;
    if (form.rental_end_date) body.rental_end_date = form.rental_end_date;
    if (form.assign_user_ids.length) body.assign_user_ids = form.assign_user_ids;
    setCreateOpen(false);
    void act(() => api.post('/vehicles', body), 'Vehicle created');
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <DirectionsCarIcon color="primary" />
        <Typography variant="h6" sx={{ fontWeight: 700, flexGrow: 1 }}>Vehicles</Typography>
        <Button variant="outlined" onClick={() => void load()} disabled={busy}>Refresh</Button>
        <Button variant="contained" onClick={() => { setForm({ type: 'company', nickname: '', license_plate: '', rental_start_date: '', rental_end_date: '', assign_user_ids: [] }); setCreateOpen(true); }}>
          New company vehicle
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        <b>Ownership is enforced on the server.</b> A vehicle a TECH creates stays on that tech's
        profile only — no other tech can see or use it — but you see it here, with its owner. A
        vehicle YOU create is a fleet vehicle: it appears in a tech's app only once you ASSIGN it
        to them (one person or many).
      </Alert>
      <Alert severity="info" sx={{ mb: 2 }}>
        <b>Fuel is a COST record, not a customer charge.</b> Per-TICKET mileage <b>is</b> billed —
        on the ERPNext <b>MILEAGE</b> line of the draft invoice (qty = miles, UOM Mile) — and its
        rate lives in <b>Admin → Billing rules</b>. Nothing on this fuel screen is invoiced.
      </Alert>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* ---------------- FUEL REPORTING (v2.3.5) ---------------- */}
      <Card sx={{ mb: 3 }} data-testid="fuel-report">
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <LocalGasStationIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 700, flexGrow: 1 }}>Fuel &amp; economy</Typography>
          {!!totals && (
            <>
              <Chip size="small" label={`${totals.fills} fills`} />
              <Chip size="small" label={`${num(totals.gallons, 1)} gal`} />
              <Chip size="small" color="primary" label={`Spend ${money(totals.spend)}`} data-testid="total-spend" />
              <Chip size="small" color="success" label={`Avg ${money3(totals.avg_cost_per_gallon)}/gal`} />
            </>
          )}
          <Button size="small" variant="outlined" onClick={() => { setGradesDraft(grades.join(', ')); setGradesOpen(true); }}>
            Fuel grades ({grades.length})
          </Button>
        </Box>
        <Divider />
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Spend per VEHICLE</Typography>
          <Table size="small" data-testid="per-vehicle">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 700 }}>Vehicle</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Fills</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Gallons</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Spend</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Cost / gal</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">MPG</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Cost / mile</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Grades used</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Last odo (mi)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {perVehicle.map((v) => (
                <TableRow key={v.vehicle_id}>
                  <TableCell>
                    <b>{v.vehicle_label}</b>{v.vehicle_plate ? ` · ${v.vehicle_plate}` : ''}
                    <Chip size="small" label={v.vehicle_type} color={TYPE_COLOR[v.vehicle_type]} sx={{ ml: 1 }} />
                  </TableCell>
                  <TableCell align="right">{v.fills}</TableCell>
                  <TableCell align="right">{num(v.gallons, 2)}</TableCell>
                  <TableCell align="right"><b>{money(v.spend)}</b></TableCell>
                  <TableCell align="right">{money3(v.avg_cost_per_gallon)}</TableCell>
                  <TableCell align="right"><b>{v.mpg === null ? '—' : num(v.mpg, 2)}</b></TableCell>
                  <TableCell align="right">{money3(v.cost_per_mile)}</TableCell>
                  <TableCell>{(v.grades || []).map(([g, n]) => <Chip key={g} size="small" label={`${g} ×${n}`} sx={{ mr: 0.5 }} />)}</TableCell>
                  <TableCell align="right">{num(v.last_odometer_miles, 0)}</TableCell>
                </TableRow>
              ))}
              {perVehicle.length === 0 && (
                <TableRow><TableCell colSpan={9} align="center">No fuel logs yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>Spend per TECH</Typography>
          <Table size="small" data-testid="per-tech">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 700 }}>Tech</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Fills</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Gallons</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Spend</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Cost / gal</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {perTech.map((t) => (
                <TableRow key={t.user_id}>
                  <TableCell><b>{t.tech_name}</b></TableCell>
                  <TableCell align="right">{t.fills}</TableCell>
                  <TableCell align="right">{num(t.gallons, 2)}</TableCell>
                  <TableCell align="right"><b>{money(t.spend)}</b></TableCell>
                  <TableCell align="right">{money3(t.avg_cost_per_gallon)}</TableCell>
                </TableRow>
              ))}
              {perTech.length === 0 && (
                <TableRow><TableCell colSpan={5} align="center">No fuel logs yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>Every fill-up</Typography>
          <Table size="small" data-testid="fuel-log-table">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 700 }}>When</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Vehicle</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Tech</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Grade</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Gallons</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Amount paid</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Cost / gal</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Odometer (mi)</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Miles since</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">MPG</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fuel.map((f) => (
                <TableRow key={f.id}>
                  <TableCell>{(f.filled_at || '').replace('T', ' ').slice(0, 16)}</TableCell>
                  <TableCell>{f.vehicle_nickname || f.vehicle_plate || `#${f.vehicle_id}`}</TableCell>
                  <TableCell>{f.tech_name || '—'}</TableCell>
                  <TableCell>{f.fuel_grade ? <Chip size="small" label={f.fuel_grade} /> : '—'}</TableCell>
                  <TableCell align="right">{num(f.gallons, 2)}</TableCell>
                  <TableCell align="right"><b>{money(f.amount_paid)}</b></TableCell>
                  <TableCell align="right">{money3(f.cost_per_gallon)}</TableCell>
                  <TableCell align="right">{num(f.odometer_miles, 0)}</TableCell>
                  <TableCell align="right">{num(f.miles_since_last_fill, 1)}</TableCell>
                  <TableCell align="right"><b>{f.mpg === null ? '—' : num(f.mpg, 2)}</b></TableCell>
                </TableRow>
              ))}
              {fuel.length === 0 && (
                <TableRow><TableCell colSpan={10} align="center">No fuel logs yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            <b>Cost/gal</b> = amount paid ÷ gallons. <b>MPG</b> = miles between consecutive odometer
            readings on the same vehicle ÷ gallons at the later fill — so a vehicle's FIRST fill has
            no MPG (no prior odometer). Volumes are stored in litres and distances in km; both are
            converted for display.
          </Typography>
        </Box>
      </Card>

      <TableContainer component={Card}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8fafc' }}>
              <TableCell sx={{ fontWeight: 700 }}>Vehicle</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Plate</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Rental period</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Entered by</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Assigned to</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Fuel</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4 }}><CircularProgress size={22} /></TableCell></TableRow>
            )}
            {!loading && vehicles.length === 0 && (
              <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                <Typography color="text.secondary">No vehicles yet.</Typography>
              </TableCell></TableRow>
            )}
            {vehicles.map((v) => {
              const logs = fuelByVehicle[v.id] || [];
              return (
                <TableRow key={v.id} hover sx={{ opacity: Number(v.active) ? 1 : 0.5 }}>
                  <TableCell sx={{ fontWeight: 600 }}>{v.nickname || `Vehicle #${v.id}`}</TableCell>
                  <TableCell><Chip size="small" label={v.type} color={TYPE_COLOR[v.type]} /></TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{v.license_plate || '—'}</TableCell>
                  <TableCell>
                    {v.type === 'rental'
                      ? `${v.rental_start_date || '?'} → ${v.rental_end_date || 'open'}`
                      : '—'}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined"
                      color={v.created_by_role === 'tech' ? 'secondary' : 'primary'}
                      label={v.created_by_role === 'tech' ? `tech: ${v.owner_name || '?'}` : 'admin (fleet)'} />
                  </TableCell>
                  <TableCell>
                    {v.assignments.length === 0
                      ? <Typography variant="caption" color="text.secondary">nobody</Typography>
                      : v.assignments.map((a) => (
                          <Chip key={a.id} size="small" sx={{ mr: 0.5, mb: 0.5 }}
                            label={a.user_name + (a.is_current ? ' (driving)' : '')} />
                        ))}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Fuel logs recorded against this vehicle">
                      <Chip size="small" icon={<LocalGasStationIcon />} label={logs.length}
                        onClick={() => setFuelFor(v)} clickable />
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={Number(v.active) ? 'Active' : 'Retired'}
                      color={Number(v.active) ? 'success' : 'default'} />
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => { setAssignOpen(v); setAssignIds(v.assignments.map((a) => a.user_id)); }}>Assign</Button>
                    {v.type === 'rental' && (
                      <Button size="small" onClick={() => { setEditOpen(v); setEditEnd(v.rental_end_date || ''); }}>End date</Button>
                    )}
                    {Number(v.active)
                      ? <Button size="small" color="error" onClick={() => void act(() => api.post(`/vehicles/${v.id}/deactivate`), 'Vehicle retired')}>Retire</Button>
                      : <Button size="small" color="success" onClick={() => void act(() => api.post(`/vehicles/${v.id}/reactivate`), 'Vehicle reactivated')}>Reactivate</Button>}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ---------- fuel logs (all) ---------- */}
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 4, mb: 1 }}>
        Fuel logs — all techs ({fuel.length})
      </Typography>
      <TableContainer component={Card}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8fafc' }}>
              <TableCell sx={{ fontWeight: 700 }}>When</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Tech</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Vehicle</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">Gallons</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">Litres</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">Odometer (mi)</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Source</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {fuel.length === 0 && (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                <Typography color="text.secondary">No fuel logs yet.</Typography>
              </TableCell></TableRow>
            )}
            {fuel.map((f) => (
              <TableRow key={f.id} hover>
                <TableCell>{(f.filled_at || '').replace('T', ' ').slice(0, 16)}</TableCell>
                <TableCell>{f.tech_name || `User ${f.user_id}`}</TableCell>
                <TableCell>{f.vehicle_nickname || f.vehicle_plate || `#${f.vehicle_id}`}</TableCell>
                <TableCell align="right">{f.gallons ?? '—'}</TableCell>
                <TableCell align="right">{f.liters ?? '—'}</TableCell>
                <TableCell align="right">{f.odometer_miles ?? '—'}</TableCell>
                <TableCell>
                  <Chip size="small" variant="outlined"
                    label={Number(f.queued_offline) ? 'synced from offline queue' : 'live'} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ---------- create ---------- */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>New vehicle</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select label="Type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                  <MenuItem value="company">Company</MenuItem>
                  <MenuItem value="rental">Rental</MenuItem>
                  <MenuItem value="personal">Personal</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label={form.type === 'company' ? 'Nickname (REQUIRED)' : 'Nickname'}
                value={form.nickname} onChange={(e) => setForm({ ...form, nickname: e.target.value })}
                error={form.type === 'company' && !form.nickname.trim()} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label={form.type === 'company' ? 'License plate (REQUIRED)' : 'License plate'}
                value={form.license_plate} onChange={(e) => setForm({ ...form, license_plate: e.target.value })}
                error={form.type === 'company' && !form.license_plate.trim()} />
            </Grid>
            {form.type === 'rental' && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" type="date" label="Rental START (REQUIRED)"
                    InputLabelProps={{ shrink: true }} value={form.rental_start_date}
                    onChange={(e) => setForm({ ...form, rental_start_date: e.target.value })}
                    error={!form.rental_start_date} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" type="date" label="Rental END (optional — editable later)"
                    InputLabelProps={{ shrink: true }} value={form.rental_end_date}
                    onChange={(e) => setForm({ ...form, rental_end_date: e.target.value })} />
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Assign to personnel (optional, one or many)</InputLabel>
                <Select multiple value={form.assign_user_ids} input={<OutlinedInput label="Assign to personnel (optional, one or many)" />}
                  onChange={(e) => setForm({ ...form, assign_user_ids: e.target.value as number[] })}
                  renderValue={(sel) => (sel as number[]).map((id) => {
                    const e2 = employees.find((x) => x.id === id);
                    return e2 ? empName(e2) : id;
                  }).join(', ')}>
                  {employees.map((e) => (
                    <MenuItem key={e.id} value={e.id}>
                      <Checkbox size="small" checked={form.assign_user_ids.indexOf(e.id) > -1} />
                      <ListItemText primary={empName(e)} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Alert severity="info" sx={{ mt: 2 }}>
            A COMPANY vehicle requires BOTH a nickname and a license plate (permanent
            identification). A RENTAL requires a start date; the end date may be added later.
            These rules are validated on the server.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!createValid() || busy} onClick={submitCreate}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* ---------- assign ---------- */}
      <Dialog open={!!assignOpen} onClose={() => setAssignOpen(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Assign “{assignOpen?.nickname || assignOpen?.license_plate}” to personnel
        </DialogTitle>
        <DialogContent dividers>
          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel>Personnel</InputLabel>
            <Select multiple value={assignIds} input={<OutlinedInput label="Personnel" />}
              onChange={(e) => setAssignIds(e.target.value as number[])}
              renderValue={(sel) => (sel as number[]).map((id) => {
                const e2 = employees.find((x) => x.id === id);
                return e2 ? empName(e2) : id;
              }).join(', ')}>
              {employees.map((e) => (
                <MenuItem key={e.id} value={e.id}>
                  <Checkbox size="small" checked={assignIds.indexOf(e.id) > -1} />
                  <ListItemText primary={empName(e)} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Unchecking a person unassigns the vehicle from them. The vehicle then disappears from
            their app.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAssignOpen(null)}>Cancel</Button>
          <Button variant="contained" disabled={busy} onClick={() => {
            const v = assignOpen!;
            const before = v.assignments.map((a) => a.user_id);
            const add = assignIds.filter((id) => before.indexOf(id) < 0);
            const remove = before.filter((id) => assignIds.indexOf(id) < 0);
            setAssignOpen(null);
            void act(async () => {
              if (add.length) await api.post(`/vehicles/${v.id}/assign`, { user_ids: add });
              for (const uid of remove) await api.post(`/vehicles/${v.id}/unassign`, { user_id: uid });
            }, 'Assignments updated');
          }}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* ---------- rental end date ---------- */}
      <Dialog open={!!editOpen} onClose={() => setEditOpen(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Rental end date</DialogTitle>
        <DialogContent dividers>
          <TextField fullWidth size="small" type="date" label="Rental END"
            InputLabelProps={{ shrink: true }} value={editEnd} onChange={(e) => setEditEnd(e.target.value)} />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Start: {editOpen?.rental_start_date || '—'}. The end date is optional at creation and
            editable here at any time.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEditOpen(null)}>Cancel</Button>
          <Button variant="contained" disabled={busy} onClick={() => {
            const v = editOpen!;
            setEditOpen(null);
            void act(() => api.patch(`/vehicles/${v.id}`, { rental_end_date: editEnd || null }), 'Rental end date saved');
          }}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* ---------- fuel per vehicle ---------- */}
      <Dialog open={!!fuelFor} onClose={() => setFuelFor(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Fuel — {fuelFor?.nickname || fuelFor?.license_plate}
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>Fuel is a COST record — not billed to a customer.</Alert>
          {(() => {
            const r = perVehicle.find((x) => x.vehicle_id === fuelFor?.id);
            return r ? (
              <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip size="small" label={`${r.fills} fills`} />
                <Chip size="small" label={`${num(r.gallons, 2)} gal`} />
                <Chip size="small" color="primary" label={`Spend ${money(r.spend)}`} />
                <Chip size="small" color="success" label={`${money3(r.avg_cost_per_gallon)}/gal`} />
                <Chip size="small" color="secondary" label={`${r.mpg === null ? '—' : num(r.mpg, 2)} MPG`} />
              </Box>
            ) : null;
          })()}
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell><TableCell>Tech</TableCell><TableCell>Grade</TableCell>
                <TableCell align="right">Gal</TableCell><TableCell align="right">Paid</TableCell>
                <TableCell align="right">$/gal</TableCell><TableCell align="right">Odo (mi)</TableCell>
                <TableCell align="right">MPG</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(fuelFor ? fuelByVehicle[fuelFor.id] || [] : []).map((f) => (
                <TableRow key={f.id}>
                  <TableCell>{(f.filled_at || '').replace('T', ' ').slice(0, 16)}</TableCell>
                  <TableCell>{f.tech_name}</TableCell>
                  <TableCell>{f.fuel_grade || '—'}</TableCell>
                  <TableCell align="right">{num(f.gallons, 2)}</TableCell>
                  <TableCell align="right">{money(f.amount_paid)}</TableCell>
                  <TableCell align="right">{money3(f.cost_per_gallon)}</TableCell>
                  <TableCell align="right">{num(f.odometer_miles, 0)}</TableCell>
                  <TableCell align="right">{f.mpg === null ? '—' : num(f.mpg, 2)}</TableCell>
                </TableRow>
              ))}
              {(!fuelFor || (fuelByVehicle[fuelFor.id] || []).length === 0) && (
                <TableRow><TableCell colSpan={8} align="center">No fuel logs.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}><Button onClick={() => setFuelFor(null)}>Close</Button></DialogActions>
      </Dialog>

      {/* ---------- fuel grade options (ADMIN SETTING) ---------- */}
      <Dialog open={gradesOpen} onClose={() => setGradesOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Fuel grades offered in the app</DialogTitle>
        <DialogContent dividers>
          <TextField fullWidth size="small" label="Grades (comma separated)"
            value={gradesDraft} onChange={(e) => setGradesDraft(e.target.value)}
            inputProps={{ 'data-testid': 'grades-input' }} />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            These are the buttons the tech sees at the pump. Change them here — no app rebuild is
            needed; the app re-reads the list. The server rejects a fuel log whose grade is not on
            this list, and rejects an empty list.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setGradesOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={busy || !gradesDraft.trim()} onClick={() => {
            const list = gradesDraft.split(',').map((x) => x.trim()).filter(Boolean);
            setGradesOpen(false);
            void act(() => api.put('/fuel-logs/settings', { fuel_grade_options: list }), 'Fuel grades saved');
          }}>Save</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} message={toast || ''} />
    </Box>
  );
}
